import PubSub from 'pubsub-js'

import { post } from '@js/common/api'
import { trackingEvent } from '@js/common/tracking-utils'
import { UA } from '@js/common/UA'
import User from '@js/common/user'
import { getUserSessionData } from '@js/common/userSessionData'

import {
  CATALOG_REQUEST_ALLOWED_PARAMS,
  DEFAULT_EXPECTED_VALUE,
  DEFAULT_LOAN_EXPECTED_VALUE,
  DEFAULT_MONTHLY_INCOME_VALUE,
  QUIZ_CONFIG_CONVERSION_TYPES,
  STANDARD_FINANCE_STANDALONE_PARTNER_NID
} from '@f/applications/QuizApp/partials/quizConstants'
import {
  AccountLoginResponse,
  AutofillValidationState,
  BaseConvertRequestPayload,
  ConversionType,
  ConvertRequestPayload,
  IMConvertRequestPayload,
  JWT,
  ProfileRequestResponse,
  SellerConversionRequestPayload,
  StepValue
} from '@f/applications/QuizApp/partials/types'
import { QUIZ_API_URL } from '@f/applications/QuizApp/partials/url'

import {
  computeProfileUpdatePayload,
  getEquityRange,
  isFinanceTypeQuiz,
  isSellerTypeQuiz
} from './utils'

function isValidJWT(jwt: JWT): jwt is JWT {
  return Boolean(jwt?.access_token && jwt?.expires)
}

export function updatePublicId(public_id: string) {
  localStorage.setItem('wt_pid', public_id)
}

function handleUserAccountLogin(response: AccountLoginResponse) {
  trackingEvent.login({
    action: 'success',
    value: 'account-creation',
    label: 'user-account',
    location: 'quiz-checkout'
  })
  PubSub.publish('USER_LOGGED_IN', {})
  UA.doLogIn({
    token: response.JWT.access_token,
    expires: response.JWT.expires.toString(),
    public_id: response.public_id
  })
}

export function handleUpdateProfileResponse(response: AccountLoginResponse) {
  updatePublicId(response.public_id)
  if (isValidJWT(response?.JWT)) {
    handleUserAccountLogin(response)
  } else {
    trackingEvent.login({
      action: 'fail',
      value: 'account-creation',
      label: 'user-account',
      location: 'quiz-checkout'
    })
  }
}

interface ConversionRequestData {
  stepValues: StepValue[]
  trackingFlow: {
    flow_information: {
      flow_id: string
      flow_type: string
      flow_kickoff: unknown[]
      flow_step: number
    }
  }
  usersLocation?: string
  exclusiveVendorNid?: string
  conversionType: ConversionType
  flowType: string
  autofillValidation: AutofillValidationState
}

export function getConversionRequestPayload({
  stepValues,
  usersLocation,
  trackingFlow,
  exclusiveVendorNid,
  conversionType,
  flowType,
  autofillValidation
}: ConversionRequestData): ConvertRequestPayload {
  const isIMCheckout =
    window.__DOMAIN__ === 'immonet' ||
    window.__DOMAIN__ === 'immowelt' ||
    window.__DOMAIN__ === 'bauen'

  let payload = {
    ...stepValues.reduce((acc, { name, value }) => {
      if (CATALOG_REQUEST_ALLOWED_PARAMS.includes(name)) {
        let finalName = name
        let finalValue = value

        if (name === 'bauort') {
          finalName = 'delivery_region'
          finalValue = `{ "postcode": "${finalValue}" }`
        } else if (name === 'expected_value') {
          finalValue = finalValue || DEFAULT_EXPECTED_VALUE
        } else if (name === 'monthly_income' || name === 'household_income') {
          finalName = 'monthly_income'
          finalValue = finalValue || DEFAULT_MONTHLY_INCOME_VALUE
        } else if (name === 'loan_amount') {
          finalValue = finalValue || DEFAULT_LOAN_EXPECTED_VALUE
        } else if (name === 'equity') {
          finalValue = getEquityRange(finalValue as number)
        } else if (name === 'property_type') {
          finalValue = value === 'na' ? null : value
        }

        return { ...acc, [finalName]: finalValue }
      }

      return acc
    }, {})
  } as BaseConvertRequestPayload

  if (!isFinanceTypeQuiz(conversionType)) {
    payload.property_delivery_region = usersLocation || ''
    payload.type = 'my-project-quiz'
    payload.funnel = 'my-project-quiz'
    payload.selected_instance_class = 'vendor'
    payload.additional_payload = trackingFlow
    payload.vendor_handover_consent = true
  } else {
    payload.financing_handover_consent = true
    payload.financing_consultation_interest = 'yes'
    payload.funnel = 'finance'
    payload.type = 'finance'
    const { city, state } = User.getLocation()
    if (city && state) {
      payload.delivery_region_city_name = city
      payload.delivery_region_state = state.long_name
    }
  }

  if (exclusiveVendorNid) {
    const exclusiveVendorAddtionalPayload = computeProfileUpdatePayload({
      stepValues
    })
    payload.selected_instances = [exclusiveVendorNid]
    payload.entity = 'EX'
    payload = { ...payload, ...exclusiveVendorAddtionalPayload }
  }

  if (conversionType === QUIZ_CONFIG_CONVERSION_TYPES.financeStandalone) {
    payload.partner_nid = STANDARD_FINANCE_STANDALONE_PARTNER_NID
    payload.is_standalone_finance_user = true
    payload.funnel = flowType
    payload.type = flowType
  }

  if (autofillValidation) {
    Object.entries(autofillValidation).forEach(([key, value]) => {
      payload[`autofill_validation_${key}`] = value
    })
  }

  const userSessionData = getUserSessionData()

  if (isIMCheckout) {
    const {
      selected_instance_class: _,
      ...payloadWithoutSelectedInstanceClass
    } = payload

    return {
      ...payloadWithoutSelectedInstanceClass,
      session_data: userSessionData
    } as IMConvertRequestPayload
  }

  if (isSellerTypeQuiz(conversionType)) {
    const {
      vendor_handover_consent: _,
      financing_handover_consent: __,
      entity: ___,
      ...payloadWithoutConsents
    } = payload

    return {
      ...payloadWithoutConsents,
      session_data: userSessionData,
      seller_handover_consent: true,
      type: 'seller-quiz-post',
      funnel: 'seller-quiz-post'
    } as SellerConversionRequestPayload
  }

  return {
    ...payload,
    session_data: userSessionData
  } as BaseConvertRequestPayload
}

export function convertUser(conversionRequestPayload: ConvertRequestPayload) {
  return post<ProfileRequestResponse, ConvertRequestPayload>(
    QUIZ_API_URL.convertUser,
    conversionRequestPayload
  )
}
